
import React from 'react';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface InsightCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  className?: string;
  accentColor?: 'primary' | 'accent' | 'green' | 'red' | 'blue';
}

const InsightCard: React.FC<InsightCardProps> = ({
  title,
  value,
  description,
  icon,
  trend,
  trendValue,
  className,
  accentColor = 'primary'
}) => {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-800 border-primary-200',
    accent: 'bg-accent-50 text-accent-800 border-accent-200',
    green: 'bg-green-50 text-green-800 border-green-200',
    red: 'bg-red-50 text-red-800 border-red-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
  };
  
  const trendIcons = {
    up: (
      <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    ),
    down: (
      <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    ),
    neutral: (
      <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
      </svg>
    )
  };
  
  return (
    <Card className={cn("overflow-hidden border-surface-border hover:shadow-md transition-shadow", className)}>
      <div className={`h-1 ${colorClasses[accentColor].split(' ')[0]} ${colorClasses[accentColor].split(' ')[1]}`} />
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <h4 className="text-sm font-medium text-text-secondary">{title}</h4>
          {icon && (
            <div className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center",
              colorClasses[accentColor]
            )}>
              {icon}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-end space-x-2">
          <div className="text-2xl font-sora font-bold">{value}</div>
          
          {trend && trendValue && (
            <div className={cn(
              "flex items-center text-sm",
              trend === 'up' ? 'text-green-600' : 
              trend === 'down' ? 'text-red-600' : 
              'text-gray-600'
            )}>
              {trendIcons[trend]}
              <span className="ml-1">{trendValue}</span>
            </div>
          )}
        </div>
        
        {description && (
          <p className="text-sm text-text-secondary mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default InsightCard;
